import { capitalize } from 'lodash';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  Rfq,
  getRfqCreatedAtDate,
  getRfqTypeNameFromRfqType,
} from '@paradigm/features/src/unified-rfqs/entities/rfq';
import { hourFormat } from '@paradigm/utils/src/date';
import { isDrfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { isGrfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { Nullable } from '@paradigm/utils/types';
import { getProduct, getProductName } from '#/products/domain';
import {
  primaryProductCode,
  strategyProductCodes,
} from '#/unified-rfqs/utils/legs';
import { Product } from '#/products/types';
import { getProductCodesDescription } from '#/unified-rfqs/utils/productCodes';
import { useRfqContext } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import {
  WithFormattingProps,
  createFormattingProps,
} from '@paradigm/features/src/unified-rfqs/ui/types';
import { usePriceFormatterFn } from '#/unified-rfqs/ui/hooks/useFormattedPrice';
import { RfqType } from '#/unified-rfqs/entities/shared';

export interface RfqRowView {
  readonly asset: string;
  readonly createdAt: WithFormattingProps<Date>;
  readonly description: string;
  readonly id: WithFormattingProps<string | number>;
  readonly instrument: WithFormattingProps<Product>;
  readonly lastTrade: WithFormattingProps<Rfq>;
  readonly product: WithFormattingProps<string>;
  readonly productCodes: WithFormattingProps<readonly string[]>;
  readonly type: WithFormattingProps<RfqType>;
  readonly venue: string;
}

export const [useRfqRowView, RfqRowViewCtxProvider] =
  createSafeContext<RfqRowView>('RfqRow');

interface RfqRowProviderProps {
  readonly children: JSX.Element;
}

/**
 * Provides common column view data to the Rfq Row. Relies on <RfqProvider/>.
 **/
export function RfqRowViewProvider({ children }: RfqRowProviderProps) {
  const { rfq } = useRfqContext();
  const formatLastTradePrice = usePriceFormatterFn({
    rfqType: rfq?.type,
    productCode: rfq?.type === 'grfq' ? rfq.product_codes[0] : null,
    description: rfq?.description,
    side: null,
  });

  if (rfq === null) return children;
  return (
    <RfqRowViewCtxProvider value={createRowView({ rfq, formatLastTradePrice })}>
      {children}
    </RfqRowViewCtxProvider>
  );
}

const getProductCodes = (rfq: Rfq): Nullable<readonly string[]> =>
  isDrfq(rfq) || isGrfq(rfq) ? rfq.product_codes : null;

const idFormatter = (value: string | number) => `ID: ${value}`;
const createLastPriceFormatter =
  (formatterFn: (price: Nullable<string>) => string) => (rfq: Rfq) =>
    isGrfq(rfq) ? formatterFn(rfq.last_trade?.price ?? null) : NEGATIVE_SIGN;
const instrumentFormatter = (product: Product) => capitalize(product.kind);
const productCodesFormatter = (value: readonly string[]) =>
  getProductCodesDescription(value);

interface RowViewArgs {
  rfq: Rfq;
  formatLastTradePrice: (price: Nullable<string>) => string;
}

const createRowView = ({
  rfq,
  formatLastTradePrice,
}: RowViewArgs): RfqRowView => {
  const createdAt = getRfqCreatedAtDate(rfq)!;
  const productCodes = getProductCodes(rfq)!;
  // rfq.product_codes is [strategy_product_code, hedge_product_code] for any
  // hedged DRFQ — a single-underlying option structure with a future hedge
  // already has 2 distinct codes there. So neither the single-product
  // identity nor the "is this multi-product" decision can come from that
  // field: the identity resolves off the primary (ratio-1) strategy leg, and
  // the label set off the strategy legs only, otherwise every hedged
  // single-product RFQ would misclassify as multi-product. GRFQ is untouched
  // — it's never multi-product and has no hedge leg in product_codes.
  const productCode = isDrfq(rfq)
    ? primaryProductCode(rfq.legs) ?? productCodes[0]!
    : productCodes[0]!;
  const product = getProduct(productCode);
  const labelCodes = isDrfq(rfq)
    ? strategyProductCodes(rfq.legs)
    : productCodes;
  return {
    asset: product.currency,
    createdAt: createFormattingProps(createdAt, hourFormat),
    description: rfq.description,
    id: createFormattingProps(rfq.id, idFormatter),
    instrument: createFormattingProps(product, instrumentFormatter),
    lastTrade: createFormattingProps(
      rfq,
      createLastPriceFormatter(formatLastTradePrice),
    ),
    product: createFormattingProps(productCode, getProductName),
    productCodes: createFormattingProps(labelCodes, productCodesFormatter),
    type: createFormattingProps(rfq.type, getRfqTypeNameFromRfqType),
    venue: rfq.venue,
  };
};
